// composables
import { ref, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
// stores
import { useAuthStore } from '@/stores/AuthStore'
// composables
import useApiRequest from '@/composables/useApiRequest'
// types
import type { Agreement } from '@/types/domain/agreement'

/**
 * Composable for managing agreement data fetching and updates
 * Handles agreement data loading, access control, and updates
 */
export default function useAgreementData(agreementID: Ref<string | number>) {
  // stores
  const authStore = useAuthStore()

  // state
  const agreementData = ref<Agreement | null>(null)
  const pageErrors = ref<Array<{ message: string }>>([])
  const dealershipIDs = ref<number[]>([])

  // api requests
  const { isLoading: agreementIsLoading, send: agreementFetchData } = useApiRequest()
  const updateAgreement = useApiRequest()

  /**
   * Checks if user has access to view this agreement
   * @param agreementDealership - The dealership data from agreement
   * @returns boolean indicating if user has access
   */
  function checkAccess(agreementDealership: { id: number; groupId: number; manufacturerId: number }): boolean {
    const viewAllowed = authStore.accessAllowed
    const directDealershipIDs = viewAllowed.dealerships.map(dealership => dealership.id)
    const groupsAllowed = viewAllowed.groups
    const manufacturersAllowed = viewAllowed.manufacturers

    dealershipIDs.value = directDealershipIDs

    const hasDirectDealershipAccess = directDealershipIDs.includes(parseInt(String(agreementDealership.id)))
    const hasIndirectDealershipAccess =
      groupsAllowed.includes(String(agreementDealership.groupId)) &&
      manufacturersAllowed.includes(String(agreementDealership.manufacturerId))

    return hasDirectDealershipAccess || hasIndirectDealershipAccess
  }

  /**
   * Fetches agreement data from the API
   * @returns Promise that resolves when data is fetched
   */
  async function fetchAgreementData(): Promise<void> {
    const response = await agreementFetchData({ endpoint: '/v1/agreements/' + agreementID.value })

    if (!response?.success) {
      return
    }

    agreementData.value = response.data as Agreement

    if (!agreementData.value) {
      return
    }

    // Check access control
    if (!checkAccess(agreementData.value.dealership)) {
      pageErrors.value.push({
        message: "You don't have access to view this agreement."
      })
      return
    }

    // Update document title
    const customerName = agreementData.value.customer.isCompany
      ? agreementData.value.customer.companyName
      : agreementData.value.customer.name
    document.title = document.title + ' | ' + customerName
  }

  /**
   * Updates agreement data via API
   * @param data - Data to update (can be object or JSON string)
   * @returns Promise that resolves with the response
   */
  async function updateAgreementData(data: Record<string, unknown> | string): Promise<unknown> {
    if (!data) {
      return Promise.resolve(null)
    }

    return new Promise((resolve, reject) => {
      const dataToSend = typeof data === 'string' ? data : JSON.stringify(data)

      updateAgreement
        .send({ endpoint: '/v1/agreements/' + agreementID.value, method: 'PATCH', data: dataToSend })
        .then(response => {
          resolve(response)

          if (response.success) {
            alertToast('Details updated', null, 'success')
          }
        })
        .catch((err: Error) => {
          reject(err)
          alertToast('Failed to update details', err.message, 'error')
        })
    })
  }

  return {
    // state
    agreementData,
    pageErrors,
    dealershipIDs,
    agreementIsLoading,
    // methods
    fetchAgreementData,
    updateAgreementData,
    checkAccess
  }
}
